import { useEffect, useState } from "react";
import { SMALL_BREAKPOINT } from "../components/edit/workspace/breakpoints";

/**
 * Detects if the user is on a mobile device by checking the user agent
 */
const isMobileUserAgent = (): boolean => {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for common mobile device indicators in user agent
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);
};

/**
 * Hook that detects if the user is on a mobile device or has a slim screen
 *
 * @returns An object containing:
 * - `isMobile`: true if mobile user agent is detected
 * - `isSlimScreen`: true if window width is below the mobile breakpoint
 * - `isMobileOrSlim`: true if either condition is met
 */
export const useIsMobileOrSlimScreen = (breakpoint = SMALL_BREAKPOINT) => {
  const [isMobile, setIsMobile] = useState(() => isMobileUserAgent());
  const [isSlimScreen, setIsSlimScreen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    // Check mobile user agent on mount (in case SSR)
    setIsMobile(isMobileUserAgent());

    // Handle window resize for slim screen detection
    const handleResize = () => {
      setIsSlimScreen(window.innerWidth < breakpoint);
    };

    // Set initial value
    handleResize();

    // Listen for resize events
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return {
    isMobile,
    isSlimScreen,
    isMobileOrSlim: isMobile || isSlimScreen,
  };
};
